// Accessor, Setter and SetStoreFunction are local stubs until solid-js dependency is available or types are migrated to React.
type Accessor<T> = () => T;
type Setter<T> = (value: T) => void;
type SetStoreFunction<T> = (value: Partial<T>) => void;
// getInvestmentMallAnimationData and isInvestmentMallSearch are local stubs until lib utilities are migrated.
const getInvestmentMallAnimationData = (): Record<string, unknown> | null => null;
const isInvestmentMallSearch = (): boolean => false;
// getPopularSearchConfigData and getSearchAnimationData are local stubs until InsightSearchFunctions are migrated.
const getPopularSearchConfigData = (): Record<string, unknown> | null => null;
const getSearchAnimationData = (): Record<string, unknown> | null => null;
// The following types are local stubs until InsightSearchTypes are migrated to this package.
type AnimationTab = {
  desktopAnimationText?: Record<string, unknown>;
  mobileAnimationText?: Record<string, unknown>;
  noOfLoops?: number;
  desktopTimestamp?: number;
  mobileTimestamp?: number;
};
type AnimationConstantsTypes = { tabs?: AnimationTab } | null | undefined;
type AnimationTextTypes = { desktopAnimationStrings?: string; mobileAnimationStrings?: string };
type ISearchObj = { title?: string };
type InsightSearchModalConstantsTypes = Record<string, unknown>;
type ListingIconInt = Record<string, unknown>;

type PopularSearchItem = { popularSearchText?: string };
type PopularSearchConfig = {
  popularSearch?: PopularSearchItem[];
  recommendedSearch?: PopularSearchItem[];
  popularSearchHeading?: string;
  recommendedSearchHeading?: string;
};

const psFilterData = (items: PopularSearchItem[] | undefined): ISearchObj[] => {
  if (!items) return [];
  return Object.values(items).map((item) => ({ title: item.popularSearchText }));
};

const getConfigProperty = (
  data: InsightSearchModalConstantsTypes | null,
  propKey: string,
  defaultValue: string
): string => {
  const val = data?.[propKey];
  return typeof val === "string" ? val : defaultValue;
};

const applyAnimationData = (
  data: InsightSearchModalConstantsTypes,
  parseData: AnimationConstantsTypes
): void => {
  data.desktopAnimationText = desktopAnimationText(parseData);
  data.mobileAnimationText = mobileAnimationText(parseData);
  data.noOfLoops = parseData?.tabs?.noOfLoops;
  data.desktopTimestamp = parseData?.tabs?.desktopTimestamp;
  data.mobileTimestamp = parseData?.tabs?.mobileTimestamp;
};

const applyPopularSearchData = (
  data: InsightSearchModalConstantsTypes,
  psConfigData: PopularSearchConfig
): void => {
  data.popularSearch = psFilterData(psConfigData.popularSearch);
  data.popularSearchTitle = psConfigData.popularSearchHeading;
  data.recommendedSearchTitle = psConfigData.recommendedSearchHeading;
  data.recommendedSearch = psFilterData(psConfigData.recommendedSearch);
};

export const InsightSearchConstants = (
  propsData?: InsightSearchModalConstantsTypes,
  setPropsData?: SetStoreFunction<InsightSearchModalConstantsTypes>
): InsightSearchModalConstantsTypes => {
  const data = propsData ?? null;
  const listIconDataKey = data
    ? (Object.keys(data).find((item) => item.startsWith("item_")) ?? "")
    : "";

  const psConfigData = getPopularSearchConfigData() as PopularSearchConfig | null;
  if (data && psConfigData && propsData) {
    applyPopularSearchData(data, psConfigData);
    const parseData = getSearchAnimationData() as AnimationConstantsTypes;
    applyAnimationData(data, parseData);
    setPropsData?.({
      ...propsData,
      metaContent: getConfigProperty(data, "metaContent", ""),
      popularSearch: psFilterData(psConfigData.popularSearch) ?? [],
      recommendedSearch: psFilterData(psConfigData.recommendedSearch) ?? [],
      searchResultUrl: getConfigProperty(data, "searchResultUrl", ""),
      searchPlaceholderText: getConfigProperty(data, "searchPlaceholderText", ""),
      popularSearchTitle: getConfigProperty(data, "popularSearchTitle", ""),
      recommendedSearchTitle: getConfigProperty(data, "recommendedSearchTitle", ""),
      recentSearchTitle: getConfigProperty(data, "recentSearchTitle", ""),
      suggestedSearchTitle: getConfigProperty(data, "suggestedSearchTitle", ""),
      recentMaxHistoryCount: (data?.recentMaxHistoryCount as number) ?? 20,
      recentMaxPreTypingCount: (data?.recentMaxPreTypingCount as number) ?? 10,
      recentMaxTypingCount: (data?.recentMaxTypingCount as number) ?? 3,
      listingIcons: (data[listIconDataKey] as ListingIconInt) ?? {},
      noOfLoops: (data?.noOfLoops as number) ?? 3,
      desktopTimestamp: parseData?.tabs?.desktopTimestamp ?? 2,
      mobileTimestamp: parseData?.tabs?.mobileTimestamp ?? 2,
      desktopAnimationText: desktopAnimationText(parseData) ?? [],
      mobileAnimationText: mobileAnimationText(parseData) ?? [],
      autoSuggestCount: (data?.autoSuggestCount as number) ?? 10,
      pimCoreAppVersion: getConfigProperty(data, "pimCoreAppVersion", "P.11.2.1"),
      maxCharSearchInput: (data?.maxCharSearchInput as number) ?? 35,
      maxChips: (data?.maxChips as number) ?? 7,
    });
  }
  return propsData ?? {};
};

const getInvestmentMallText = (
  mallData: Record<string, unknown>,
  key: "desktopAnimationText" | "mobileAnimationText"
): string[] => {
  if (typeof mallData[key] === "string") {
    return mallData[key] ? [mallData[key]] : [];
  }
  return [];
};

const desktopAnimationText = (parseData: AnimationConstantsTypes): string[] => {
  if (isInvestmentMallSearch()) {
    const mallData = getInvestmentMallAnimationData();
    if (mallData && typeof mallData === "object" && "desktopAnimationText" in mallData) {
      return getInvestmentMallText(mallData, "desktopAnimationText");
    }
  }
  return parseData?.tabs?.desktopAnimationText
    ? Object.values(parseData.tabs.desktopAnimationText).map(
        (item) => (item as AnimationTextTypes).desktopAnimationStrings ?? ""
      )
    : [];
};

const mobileAnimationText = (parseData: AnimationConstantsTypes): string[] => {
  if (isInvestmentMallSearch()) {
    const mallData = getInvestmentMallAnimationData();
    if (mallData && typeof mallData === "object" && "mobileAnimationText" in mallData) {
      return getInvestmentMallText(mallData, "mobileAnimationText");
    }
  }
  return parseData?.tabs?.mobileAnimationText
    ? Object.values(parseData.tabs.mobileAnimationText).map(
        (item) => (item as AnimationTextTypes).mobileAnimationStrings ?? ""
      )
    : [];
};

export const WEB_LOGGED_IN = "Web-logged-in";
export const WEB_NON_LOGGED_IN = "Web-non-logged-in";
export const WEB_NON_LOGGED_IN_IDENTIFIED = "Web-non-logged-in-identified";
export const SOURCE = "website";
export const PS_LOGGED_IN_URL = "/api/v4/search/logged-in";
export const PS_NON_LOGGER_IN_URL = "/api/v4/search/non-logged-in";
export const TYPEWRITER_SPEED = 100;
